/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { SelectOption, SilentSelectField, VisuallyHidden, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { Forum } from 'bundles/discussions/lib/types';

import _t from 'i18n!nls/discussions';

import { objectToQueryString } from './objectToQueryString';

const styles = (buttonProps: any) => ({
  selectField: css`
    max-width: 300px;

    .cds-silentSelect-filled {
      ${{ ...buttonProps.style }}
    }
  `,
});

export function getDropDownState<paramKeyType>(
  query: Record<string, string>,
  paramKeys: paramKeyType[]
): Record<string, unknown> {
  const result: Record<string, string> = {};

  paramKeys.forEach((key) => {
    if (typeof key === 'string' && key in query) {
      const value = query[key];
      result[key] = value || '';
    }
  });

  return result;
}

export type ForumDropDownOption<value, param> = {
  value?: value;
  param: param;
  label: string;
  forum?: Forum;
};

export interface ForumDropDownProps<value, param> {
  options: ForumDropDownOption<value, param>[];
  selectedLabel?: string;
  style?: React.CSSProperties;
  onChange?: (option: ForumDropDownOption<value, param>) => void;
  className?: string;
  theme: Theme;
  e2eId?: string;
  ariaLabelPrefix?: string;
}
type State = {
  isDropdownOpenAndSelectedItemOnFocus: boolean;
};

export class ForumDropDown<value, param> extends React.Component<ForumDropDownProps<value, param>, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof ForumDropDown.contextTypes>;

  handleChangeFilter = (event: React.ChangeEvent<{ value: unknown }>) => {
    const item = this.props.options.find((el) => el.value === event.target.value) as ForumDropDownOption<value, param>;

    if (this.props.onChange) {
      this.props.onChange(item);
    } else {
      const addedFilter = {};
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      addedFilter[item.param] = item.value;
      const queryStringOut = objectToQueryString({
        ...this.context.router.location.query,
        ...{ page: '1' },
        ...addedFilter,
      });
      const newPathName = this.context.router.location.pathname + '?' + queryStringOut;
      this.context.router.push(newPathName);
    }
  };

  state = {
    isDropdownOpenAndSelectedItemOnFocus: false,
  };

  render() {
    const { options, selectedLabel, ariaLabelPrefix, ...buttonProps } = this.props;
    const key = options && options[0]?.param;
    const selectedOptions = getDropDownState(this.context?.router?.location?.query, [key]);
    const { isDropdownOpenAndSelectedItemOnFocus } = this.state;

    if (options === undefined) {
      return null;
    }

    let results: ForumDropDownOption<value, param>[];
    if (selectedLabel) {
      results = options.filter((option) => option?.label === selectedLabel);
    } else {
      results = options.filter((option) => option.value === selectedOptions[String(key)]);
    }

    let selectedOption = results.shift();

    if (!selectedOption) {
      selectedOption = options[0];
    }

    return (
      <SilentSelectField
        label={
          ariaLabelPrefix ? _t('#{ariaLabelPrefix} selection dropdown. Current selection', { ariaLabelPrefix }) : ''
        }
        value={selectedOption.value as string}
        labelPlacement="none"
        data-testid={this.props.e2eId}
        onChange={this.handleChangeFilter}
        onOpen={() => {
          this.setState({ isDropdownOpenAndSelectedItemOnFocus: true });
        }}
        onClose={() => {
          this.setState({ isDropdownOpenAndSelectedItemOnFocus: false });
        }}
        css={styles(buttonProps).selectField}
      >
        {options.map((item) => (
          <SelectOption
            value={item.value as string}
            key={item.label + item.value}
            onKeyDown={() => this.setState({ isDropdownOpenAndSelectedItemOnFocus: false })}
          >
            {selectedOption.value === item.value && isDropdownOpenAndSelectedItemOnFocus && (
              <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
                {ariaLabelPrefix
                  ? _t('Dropdown expanded. Inside list of options for #{ariaLabelPrefix}. :', {
                      ariaLabelPrefix,
                    })
                  : _t('Dropdown expanded. :')}
              </VisuallyHidden>
            )}
            {item.label}
          </SelectOption>
        ))}
      </SilentSelectField>
    );
  }
}

export function ForumDropDownWithTheme<value, param>(props: Omit<ForumDropDownProps<value, param>, 'theme'>) {
  const theme = useTheme();
  return <ForumDropDown<value, param> {...props} theme={theme} />;
}
